import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { resolveSupportAssetUrl } from '../../../utils/supportAssets';

const QUICK_LINKS = [
    { label: 'Create your first project', href: '/dashboard/new-translation', desc: 'Upload a source file and pick target languages.' },
    { label: 'Invite teammates', href: '/dashboard/people', desc: 'Add translators, reviewers, and admins.' },
    { label: 'Connect a source', href: '/dashboard/apps', desc: 'Sync from Drive, Dropbox, GitHub, Figma.' },
    { label: 'Set your brand voice', href: '/dashboard/brand-voice', desc: 'Lock tone and terminology across languages.' },
];

const Help = () => {
    const [categories, setCategories] = useState([]);
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        axios
            .get('/api/support/categories')
            .then((r) => {
                setCategories(Array.isArray(r.data) ? r.data : []);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return categories;
        return categories
            .map((c) => ({
                ...c,
                articles: (c.articles || []).filter((a) =>
                    (a.title || '').toLowerCase().includes(s) || (a.summary || '').toLowerCase().includes(s)),
            }))
            .filter((c) => (c.articles || []).length > 0 || (c.name || '').toLowerCase().includes(s));
    }, [categories, q]);

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">Help center</h1>
                <div className="rs-page-actions">
                    <button type="button" className="btn-acc" onClick={() => navigate('/dashboard/support')}>
                        Contact support
                    </button>
                </div>
            </div>
            <p className="rs-page-sub">Guides, troubleshooting, and answers to common questions.</p>

            <div style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--line)',
                borderRadius: 14,
                padding: 24,
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
            }}>
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                    <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
                <input
                    placeholder="Search articles…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    style={{
                        flex: 1,
                        border: 0,
                        background: 'transparent',
                        fontSize: 14,
                        outline: 'none',
                        color: 'var(--ink)',
                        fontFamily: 'inherit',
                    }}
                />
            </div>

            <div className="rs-card" style={{ marginBottom: 16 }}>
                <div className="rs-card-h">
                    <h3>Quick start</h3>
                </div>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: 12,
                }}>
                    {QUICK_LINKS.map((l) => (
                        <button
                            key={l.href}
                            type="button"
                            onClick={() => navigate(l.href)}
                            style={{
                                textAlign: 'left',
                                background: 'var(--bg-soft)',
                                border: '1px solid var(--line)',
                                borderRadius: 10,
                                padding: 14,
                                cursor: 'pointer',
                                color: 'inherit',
                                fontFamily: 'inherit',
                            }}
                        >
                            <b style={{ display: 'block', fontSize: 13.5, color: 'var(--ink)' }}>{l.label}</b>
                            <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
                                {l.desc}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading articles…</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {filtered.length === 0 && (
                        <div className="rs-card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>
                            No articles match your search.
                        </div>
                    )}
                    {filtered.map((c) => (
                        <div key={c.id || c._id || c.name} className="rs-card">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
                                {c.featuredImageUrl && (
                                    <img
                                        src={resolveSupportAssetUrl(c.featuredImageUrl)}
                                        alt=""
                                        loading="lazy"
                                        style={{
                                            width: 84,
                                            height: 48,
                                            objectFit: 'cover',
                                            borderRadius: 8,
                                            border: '1px solid var(--line)',
                                            flex: '0 0 auto',
                                        }}
                                    />
                                )}
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div className="rs-card-h" style={{ marginBottom: 0 }}>
                                        <h3>{c.name}</h3>
                                        <span className="count">{(c.articles || []).length}</span>
                                    </div>
                                    {c.description && (
                                        <p style={{ margin: '3px 0 0', fontSize: 12.5, color: 'var(--ink-3)' }}>
                                            {c.description}
                                        </p>
                                    )}
                                </div>
                            </div>
                            {(c.articles || []).length === 0 ? (
                                <p style={{ color: 'var(--ink-4)', fontSize: 13 }}>No articles yet.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {(c.articles || []).map((a) => (
                                        <button
                                            key={a.slug || a._id}
                                            type="button"
                                            onClick={() => navigate(`/dashboard/help/${a.slug || a._id}`)}
                                            style={{
                                                textAlign: 'left',
                                                background: 'transparent',
                                                border: 0,
                                                borderTop: '1px solid var(--line)',
                                                padding: '10px 0',
                                                cursor: 'pointer',
                                                fontFamily: 'inherit',
                                                color: 'inherit',
                                            }}
                                        >
                                            <b style={{ fontSize: 13.5, color: 'var(--ink)' }}>{a.title}</b>
                                            {a.summary && (
                                                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                                                    {a.summary}
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default Help;
